import { useAuth, useOrganization, useUser } from "@clerk/clerk-react";
import {
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { ActivityModal, activeActivityCount } from "./ActivityModal.js";
import { AgentChat } from "./AgentChat.js";
import {
	fetchDeskChatHistory,
	persistDeskChatTurn,
	streamAgentChat,
} from "./agent-chat-api.js";
import { AgentInfoModal } from "./AgentInfoModal.js";
import { CommandPalette } from "./CommandPalette.js";
import { AgentsHub } from "./AgentsHub.js";
import {
	ChannelsHub,
	ConnectorsHub,
	type ChannelsFilter,
	type ChannelsView,
	type ConnectorsFilter,
	type ConnectorsView,
} from "./ConnectorsHub.js";
import { SitesHub } from "./SitesHub.js";
import { suggestionsFor } from "./agent-suggestions.js";
import { IntegrationDetail } from "./IntegrationDetail.js";
import { ensurePlatformSession, PLATFORM_TO_MATRIX } from "./connections-api.js";
import { usePlatformConnections } from "./usePlatformConnections.js";
import { MairkoLoader } from "./MairkoLoader.js";
import { OrgSwitcher } from "./OrgSwitcher.js";
import { PlanBadge } from "./PlanBadge.js";
import { usePlatformSession } from "./PlatformSession.js";
import { SidebarNeedsYou } from "./SidebarNeedsYou.js";
import { SidebarRecents } from "./SidebarRecents.js";
import { SidebarUsage } from "./SidebarUsage.js";
import {
	recentChatsFromMessages,
	stubPendingApprovals,
} from "./sidebar-stubs.js";
import {
	DEFAULT_NOTIFICATIONS,
	DEFAULT_RISK,
	type NotificationPrefs,
	type RiskConfig,
	type SettingsTab,
} from "./settings.js";
import {
	readDeskState,
	resolveIntegration,
	writeDeskState,
	type WorkspaceMode,
} from "./desk-state.js";
import { MAIRKO_CHAT } from "./mairko-chat.js";
import {
	MairkoChatView,
	previewUrlFromHandoffs,
	previewUrlFromSiteClones,
	type ChatSurface,
} from "./MairkoChatView.js";
import { patchFromSiteStatus } from "./clone-status.js";
import { readStoredModel } from "./chat-model.js";
import { requestChatTitle } from "./chat-title.js";
import {
	applyGeneratedMairkoTitle,
	createMairkoThread,
	deleteMairkoThread,
	readChatPlace,
	readMairkoThreads,
	renameMairkoThread,
	resetMairkoThreads,
	isInjectedSiteCard,
	threadHasResumeWork,
	threadUnlocksDesk,
	togglePinMairkoThread,
	upsertMairkoThread,
	writeChatPlace,
	writeMairkoThreads,
} from "./mairko-threads.js";
import { SettingsModal } from "./SettingsModal.js";
import { ShellToolbar } from "./ShellToolbar.js";
import { isMairkoAdmin } from "./admin.js";
import { useAppMode } from "./app-mode.js";
import { DevResetTrigger, useDevReset } from "./DevResetButton.js";
import { SignOutConfirm } from "./SignOutConfirm.js";
import {
	readLeftSidebarOpen,
	useNarrowViewport,
	writeLeftSidebarOpen,
} from "./shell.js";
import { normalizeChatFiles } from "./chat-attachments.js";
import { cn } from "./cn.js";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
	ActivityIcon,
	BellIcon,
	CreditCardIcon,
	LogOutIcon,
	SettingsIcon,
	ShieldIcon,
	UsersIcon,
} from "lucide-react";
import {
	brandFromThread,
	chatHasBackgroundWork,
	chatMessagesBusy,
	collectSiteClones,
	mergeSiteCloneLists,
	mergeSiteDesignLists,
	parseHostedPublishCommand,
	chatHistoryBody,
	siteCloneFollowUpLabel,
	siteCloneFollowUpPrompt,
	siteCloneInFlight,
	siteClonePhase,
	type CardAction,
	seedMessages,
	type ChatCompanyResearch,
	type ChatHandoff,
	type ChatMessage,
	type ChatSiteClone,
	type ChatSiteDesign,
	type ChatWorkingCopyAsk,
	type HandoffDecisionEvent,
	type PublishDecisionEvent,
	type SiteCloneCardEvent,
} from "./messages.js";
import {
	AGENTS,
	agentById,
	type AgentId,
	type Integration,
	type PrimaryNav,
} from "./nav.js";
import {
	completeOnboardingSession,
	composerEnabled,
	displayName,
	clearOnboardingSession,
	emptySession,
	extractOnboardingUrl,
	parseWorkingCopyCommand,
	resolveWorkingCopyUrl,
	isOnboardingComplete,
	progressFor,
	readOnboardingSession,
	startOnboarding,
	stepMeta,
	resumeMessages,
	seedMainChatOnboarding,
	welcomeMessages,
	writeOnboardingSession,
	type OnboardingSession,
} from "./onboarding-flow.js";
import { WORKING_COPY_FEATURE } from "./billing.js";
import {
	confirmOnboardingPack,
	fetchOnboardingStatus,
	startNewSiteBuild,
} from "./onboarding-api.js";
import {
	applyResolvedTheme,
	nextThemePreference,
	readThemePreference,
	resolveTheme,
	writeThemePreference,
	type ThemePreference,
} from "./theme.js";

function oauthFlashMessage(
	outcome: string | null,
	_provider?: string | null,
): { text: string; tone: "ok" | "err" } | null {
	if (!outcome) return null;
	if (outcome.startsWith("provider_error:")) {
		const code = outcome.slice("provider_error:".length);
		if (code === "invalid_scope") {
			return {
				text: "Cloudflare rejected the scopes (invalid_scope). Authorize must use exact catalog ids already on your OAuth client (e.g. zone.read, account-settings.read — not account.read). Enable those in Manage Account → OAuth clients → Scopes, match CLOUDFLARE_OAUTH_SCOPES, restart the platform, Connect again.",
				tone: "err",
			};
		}
		return {
			text: `Provider rejected the authorize request (${code}). Check the OAuth app callback URL and enabled scopes, then try Connect again.`,
			tone: "err",
		};
	}
	switch (outcome) {
		case "1":
			return {
				text: "Connected. Resources sync into the portfolio.",
				tone: "ok",
			};
		case "declined":
			return {
				text: "Authorization was declined (Cancel on the provider, or no code returned). Click Connect again and choose Allow.",
				tone: "err",
			};
		case "insufficient_scope":
			return {
				text: "Connected at the provider, but no usable scopes came back. Try Connect again.",
				tone: "err",
			};
		case "error":
			return {
				text: "Connection failed. Try Connect again.",
				tone: "err",
			};
		default:
			return null;
	}
}

type Phase = "gate" | "app";
/** Agents desk gallery vs a single-agent chat. */
type AgentsSurface = "hub" | "chat";

function initialAgentsSurface(session: OnboardingSession | null): AgentsSurface {
	if (!session || !isOnboardingComplete(session)) return "chat";
	return "hub";
}

function findPendingWorkingCopyAsk(
	messages: ChatMessage[],
): { ask: ChatWorkingCopyAsk; messageId: string } | null {
	for (let i = messages.length - 1; i >= 0; i--) {
		const message = messages[i];
		if (message?.workingCopyAsk && !message.workingCopyAsk.decision) {
			return { ask: message.workingCopyAsk, messageId: message.id };
		}
	}
	return null;
}

export function App() {
	const { user, isLoaded: userLoaded } = useUser();
	const { has, isLoaded: authLoaded, getToken } = useAuth();
	const { organization } = useOrganization();
	const workingCopyEntitled = Boolean(
		authLoaded && has?.({ feature: WORKING_COPY_FEATURE }),
	);
	const {
		ready: sessionReady,
		tenantName,
		setTenantName,
		clerkOrgId,
	} = usePlatformSession();
	const { enterAdmin } = useAppMode();
	const { openReset, dialog: resetDialog } = useDevReset();
	const [signOutOpen, setSignOutOpen] = useState(false);
	const [session, setSession] = useState<OnboardingSession>(
		() => readOnboardingSession() ?? emptySession(),
	);
	const [confirmingPackId, setConfirmingPackId] = useState<string | null>(
		null,
	);
	const [phase, setPhase] = useState<Phase>("app");
	const [workspace, setWorkspace] = useState<WorkspaceMode>(() => {
		const onboarding = readOnboardingSession();
		if (onboarding && !isOnboardingComplete(onboarding)) return "desk";
		return readDeskState().workspace;
	});
	const [nav, setNav] = useState<PrimaryNav>(() => readDeskState().nav);
	const [agentsSurface, setAgentsSurface] = useState<AgentsSurface>(() => {
		const onboarding = readOnboardingSession();
		if (onboarding && !isOnboardingComplete(onboarding)) {
			return initialAgentsSurface(onboarding);
		}
		return readDeskState().agentsSurface;
	});
	const [mairkoThreads, setMairkoThreads] = useState(() => readMairkoThreads());
	const [activeMairkoThreadId, setActiveMairkoThreadId] = useState(() => {
		const threads = readMairkoThreads();
		const place = readChatPlace();
		if (place && threads.some((thread) => thread.id === place.activeThreadId)) {
			return place.activeThreadId;
		}
		return threads[0]!.id;
	});
	const [mairkoDraft, setMairkoDraft] = useState("");
	const [agentId, setAgentId] = useState<AgentId>(
		() => readDeskState().agentId,
	);
	const [infoAgentId, setInfoAgentId] = useState<AgentId | null>(null);
	const [selectedIntegration, setSelectedIntegration] =
		useState<Integration | null>(() =>
			resolveIntegration(readDeskState().selectedIntegrationId),
		);
	const [draft, setDraft] = useState("");
	const [typing, setTyping] = useState(false);
	const [themePref, setThemePref] = useState<ThemePreference>(() =>
		readThemePreference(),
	);
	const narrow = useNarrowViewport();
	const [leftOpen, setLeftOpen] = useState(() =>
		typeof window !== "undefined" &&
		window.matchMedia("(max-width: 720px)").matches
			? false
			: readLeftSidebarOpen(),
	);
	const [settingsOpen, setSettingsOpen] = useState(
		() => readDeskState().settingsOpen,
	);
	const [settingsTab, setSettingsTab] = useState<SettingsTab>(
		() => readDeskState().settingsTab,
	);
	const [connectorsView, setConnectorsView] = useState<ConnectorsView>(
		() => readDeskState().connectorsView,
	);
	const [connectorsFilter, setConnectorsFilter] = useState<ConnectorsFilter>(
		() => readDeskState().connectorsFilter,
	);
	const [channelsView, setChannelsView] = useState<ChannelsView>(
		() => readDeskState().channelsView,
	);
	const [channelsFilter, setChannelsFilter] = useState<ChannelsFilter>(
		() => readDeskState().channelsFilter,
	);
	const [activityOpen, setActivityOpen] = useState(false);
	const [searchOpen, setSearchOpen] = useState(false);
	const [oauthFlash, setOauthFlash] = useState<{
		text: string;
		tone: "ok" | "err";
	} | null>(null);
	/** Chat shell pane — messages vs in-shell Connectors (same hub as Desk). */
	const [chatSurface, setChatSurface] = useState<ChatSurface>(
		() => readChatPlace()?.chatSurface ?? "messages",
	);
	const [sitePreviewUrl, setSitePreviewUrl] = useState<string | null>(
		() => readChatPlace()?.sitePreviewUrl ?? null,
	);
	const connections = usePlatformConnections(true);
	const oauthHandled = useRef(false);
	const cloneReadyPinged = useRef(new Set<string>());
	const cloneCardFollowUp = useRef(new Set<string>());
	const [risk, setRisk] = useState<RiskConfig>(DEFAULT_RISK);
	const [notifications, setNotifications] =
		useState<NotificationPrefs>(DEFAULT_NOTIFICATIONS);

	const [setupDone, setSetupDone] = useState(false);
	const [setupKnown, setSetupKnown] = useState(false);
	const [workingCopyCheckout, setWorkingCopyCheckout] = useState<{
		messageId: string;
		ask: ChatWorkingCopyAsk;
	} | null>(null);

	// OAuth callback lands on Matrix with ?matrixConnector=&provider=&connected=
	// Chat Connect also sets matrixChatAgent= + oauthPopup=1 (popup closes itself).
	useEffect(() => {
		if (oauthHandled.current) return;
		const params = new URLSearchParams(window.location.search);
		const matrixConnector = params.get("matrixConnector");
		const matrixChatAgent = params.get("matrixChatAgent");
		const matrixChatConnect = params.get("matrixChatConnect");
		const oauthPopup = params.get("oauthPopup");
		const connected = params.get("connected");
		const provider = params.get("provider");
		if (
			!matrixConnector &&
			!matrixChatAgent &&
			!connected &&
			!provider
		) {
			oauthHandled.current = true;
			return;
		}

		oauthHandled.current = true;

		if (oauthPopup === "1" && window.opener) {
			try {
				window.opener.postMessage(
					{
						type: "mairko-oauth",
						provider,
						connected,
						matrixChatAgent,
					},
					window.location.origin,
				);
			} catch {
				/* ignore cross-origin */
			}
			window.close();
			return;
		}

		const flash = oauthFlashMessage(connected, provider);
		if (flash) setOauthFlash(flash);
		void connections.refresh();

		if (matrixChatAgent || matrixChatConnect === "1") {
			const agentTarget = matrixChatAgent ?? "mairko";
			if (agentTarget === "mairko") {
				setWorkspace("chat");
				const matrixId = provider
					? (PLATFORM_TO_MATRIX[provider] ?? provider)
					: null;
				if (matrixId) {
					const item =
						connections.connectors.find((c) => c.id === matrixId) ??
						resolveIntegration(matrixId);
					if (item) {
						setChatSurface("connectors");
						setSelectedIntegration(item);
					}
				}
			} else {
				setWorkspace("desk");
				setNav("agents");
				setAgentsSurface("chat");
				setAgentId(agentTarget as AgentId);
			}
		} else if (matrixConnector || provider) {
			const matrixId =
				matrixConnector ??
				(provider ? (PLATFORM_TO_MATRIX[provider] ?? provider) : null);
			if (matrixId) {
				const item =
					connections.connectors.find((c) => c.id === matrixId) ??
					resolveIntegration(matrixId);
				if (item) {
					setWorkspace("desk");
					setNav("connectors");
					setSelectedIntegration(item);
				}
			}
		}

		const url = new URL(window.location.href);
		url.searchParams.delete("matrixConnector");
		url.searchParams.delete("matrixChatAgent");
		url.searchParams.delete("matrixChatConnect");
		url.searchParams.delete("oauthPopup");
		url.searchParams.delete("connected");
		url.searchParams.delete("provider");
		window.history.replaceState(
			{},
			"",
			`${url.pathname}${url.search}${url.hash}`,
		);
	}, [connections.connectors, connections.refresh]);

	// Keep open detail status in sync with live /api/connections.
	useEffect(() => {
		if (!selectedIntegration) return;
		const live =
			connections.connectors.find((c) => c.id === selectedIntegration.id) ??
			connections.channels.find((c) => c.id === selectedIntegration.id);
		if (!live) return;
		if (
			live.status !== selectedIntegration.status ||
			live.name !== selectedIntegration.name
		) {
			setSelectedIntegration(live);
		}
	}, [connections.connectors, connections.channels, selectedIntegration]);

	const pendingApprovals = useMemo(
		() => stubPendingApprovals(setupDone),
		[setupDone],
	);

	const [messagesByAgent, setMessagesByAgent] = useState<
		Record<string, ChatMessage[]>
	>(() => {
		const existing = readOnboardingSession() ?? emptySession();
		const initial: Record<string, ChatMessage[]> = {};
		for (const agent of AGENTS) {
			initial[agent.id] =
				agent.id === "onboarding"
					? resumeMessages(existing)
					: seedMessages(agent.id);
		}
		return initial;
	});

	const recentChats = useMemo(
		() => recentChatsFromMessages(messagesByAgent, 5),
		[messagesByAgent],
	);

	/** Desk agent chats with an in-flight reply or background job — sidebar spinner. */
	const busyAgentIds = useMemo(() => {
		const ids = new Set<AgentId>();
		for (const [id, messages] of Object.entries(messagesByAgent)) {
			if (chatHasBackgroundWork(messages)) ids.add(id as AgentId);
		}
		return ids;
	}, [messagesByAgent]);

	const activeMairkoThread = useMemo(
		() => mairkoThreads.find((t) => t.id === activeMairkoThreadId) ?? null,
		[mairkoThreads, activeMairkoThreadId],
	);

	const pingWorkingCopyReady = (
		siteId: string,
		name: string,
		previewUrl: string | null | undefined,
		threadId: string = activeMairkoThreadId,
	) => {
		if (cloneReadyPinged.current.has(siteId)) return;
		cloneReadyPinged.current.add(siteId);
		if (previewUrl && threadId === activeMairkoThreadId) {
			setSitePreviewUrl(previewUrl);
		}
		const body = `Your working copy of ${name} is ready. Open Preview whenever you want.`;
		const ping: ChatMessage = {
			id: crypto.randomUUID(),
			agentId: "mairko",
			author: "agent",
			authorName: MAIRKO_CHAT.name,
			body,
			at: new Date().toISOString(),
		};
		setMairkoThreads((prev) => {
			const current = prev.find((t) => t.id === threadId);
			if (!current) return prev;
			if (
				current.messages.some(
					(row) => row.body === body && row.author === "agent",
				)
			) {
				return prev;
			}
			return upsertMairkoThread(prev, {
				...current,
				messages: [...current.messages, ping],
			});
		});
		void persistDeskChatTurn({
			agentId: "mairko",
			threadId,
			content: body,
		});
	};

	/** Only an open model stream blocks the composer (not clone/research cards). */
	const mairkoTyping = chatMessagesBusy(activeMairkoThread?.messages ?? []);

	const activityBusy = activeActivityCount(setupDone, session.step);

	// First-run lives in the main chat — never restore Desk / catalogs.
	useEffect(() => {
		if (setupDone) return;
		if (workspace !== "chat") setWorkspace("chat");
		if (chatSurface !== "messages") setChatSurface("messages");
		if (selectedIntegration) setSelectedIntegration(null);
	}, [setupDone, workspace, chatSurface, selectedIntegration]);

	useEffect(() => {
		applyResolvedTheme(resolveTheme(themePref));
		writeThemePreference(themePref);
	}, [themePref]);

	useEffect(() => {
		if (themePref !== "system") return;
		const mq = window.matchMedia("(prefers-color-scheme: dark)");
		const onChange = () => applyResolvedTheme(resolveTheme("system"));
		mq.addEventListener("change", onChange);
		return () => mq.removeEventListener("change", onChange);
	}, [themePref]);

	useEffect(() => {
		writeOnboardingSession(session);
	}, [session]);

	// Clerk is source of truth for identity; keep Matrix session profile in sync.
	useEffect(() => {
		if (!userLoaded || !user) return;
		const email =
			user.primaryEmailAddress?.emailAddress ??
			user.emailAddresses[0]?.emailAddress ??
			"";
		const firstName = user.firstName ?? "";
		const lastName = user.lastName ?? "";
		setSession((prev) => {
			if (
				prev.profile.firstName === firstName &&
				prev.profile.lastName === lastName &&
				prev.profile.email === email
			) {
				return prev;
			}
			return {
				...prev,
				profile: {
					...prev.profile,
					firstName: firstName || prev.profile.firstName,
					lastName: lastName || prev.profile.lastName,
					email: email || prev.profile.email,
				},
			};
		});
	}, [userLoaded, user]);

	useEffect(() => {
		if (narrow) return;
		writeLeftSidebarOpen(leftOpen);
	}, [leftOpen, narrow]);

	useEffect(() => {
		if (narrow) setLeftOpen(false);
	}, [narrow]);

	useEffect(() => {
		writeDeskState({
			workspace,
			nav,
			agentsSurface,
			agentId,
			agentsView: "list",
			agentsFilter: "all",
			connectorsView,
			connectorsFilter,
			channelsView,
			channelsFilter,
			settingsOpen,
			settingsTab,
			selectedIntegrationId: selectedIntegration?.id ?? null,
		});
	}, [
		workspace,
		nav,
		agentsSurface,
		agentId,
		connectorsView,
		connectorsFilter,
		channelsView,
		channelsFilter,
		settingsOpen,
		settingsTab,
		selectedIntegration,
	]);

	useEffect(() => {
		writeMairkoThreads(mairkoThreads);
	}, [mairkoThreads]);

	useEffect(() => {
		writeChatPlace({
			activeThreadId: activeMairkoThreadId,
			sitePreviewUrl,
			chatSurface,
		});
	}, [activeMairkoThreadId, sitePreviewUrl, chatSurface]);

	const threadsRef = useRef(mairkoThreads);
	threadsRef.current = mairkoThreads;

	useEffect(() => {
		const thread =
			threadsRef.current.find((row) => row.id === activeMairkoThreadId) ??
			null;
		setSitePreviewUrl((open) => {
			if (!open) return open;
			const owned = collectSiteClones(thread?.messages ?? []).some(
				(clone) =>
					clone.previewUrl === open || open.includes(clone.siteId),
			);
			return owned ? open : null;
		});
	}, [activeMairkoThreadId]);

	/**
	 * Reconnect only: if this thread has no in-memory messages, load DeskChatAgent.
	 * Do NOT re-fetch when typing ends — that raced the async DO persist and wiped
	 * the just-streamed assistant turn.
	 */
	useEffect(() => {
		if (phase !== "app") return;
		const threadId = activeMairkoThreadId;
		const local = mairkoThreads.find((t) => t.id === threadId);
		if (!local || local.messages.length > 0) return;
		let cancelled = false;
		void (async () => {
			const hist = await fetchDeskChatHistory({
				agentId: "mairko",
				threadId,
			});
			if (cancelled || !hist.ok || hist.recent.length === 0) return;
			setMairkoThreads((prev) => {
				const current = prev.find((t) => t.id === threadId);
				// Another send may have populated the thread while we fetched.
				if (!current || current.messages.length > 0) return prev;
				const messages: ChatMessage[] = hist.recent.map((row) => ({
					id: crypto.randomUUID(),
					agentId: "mairko",
					author: row.role === "user" ? "human" : "agent",
					authorName: row.role === "user" ? displayName(session) : "Mairko",
					body: row.content,
					at: row.at,
				}));
				return upsertMairkoThread(prev, {
					...current,
					messages,
					updatedAt: messages.at(-1)?.at ?? current.updatedAt,
				});
			});
		})();
		return () => {
			cancelled = true;
		};
		// Only on thread focus / app entry — not on every local message write.
		// eslint-disable-next-line react-hooks/exhaustive-deps -- intentional
	}, [activeMairkoThreadId, setupDone, phase]);

	/**
	 * Drop orphaned pending bubbles (stream died / OOM) so the composer unlocks,
	 * and re-attach hosted clone cards that history text can’t restore.
	 */
	useEffect(() => {
		if (phase !== "app") return;
		const threadId = activeMairkoThreadId;
		const STALE_PENDING_MS = 90_000;

		setMairkoThreads((prev) =>
			prev.map((thread) => {
				if (thread.id !== threadId) return thread;
				if (!thread.messages.some((row) => row.pending)) return thread;
				const now = Date.now();
				let touched = false;
				const messages = thread.messages.map((row) => {
					if (!row.pending) return row;
					const age = now - new Date(row.at).getTime();
					if (Number.isFinite(age) && age < STALE_PENDING_MS) return row;
					touched = true;
					return {
						...row,
						pending: false,
						body:
							row.body.trim() ||
							"That reply didn’t finish — try again whenever you’re ready.",
					};
				});
				return touched ? { ...thread, messages } : thread;
			}),
		);

		let cancelled = false;
		void (async () => {
			try {
				const response = await fetch("/api/sites", { credentials: "include" });
				if (!response.ok || cancelled) return;
				const json: unknown = await response.json();
				if (!json || typeof json !== "object") return;
				const sites = (json as { ok?: unknown; sites?: unknown }).sites;
				if (!Array.isArray(sites) || sites.length === 0) return;

				const clones: ChatSiteClone[] = [];
				for (const raw of sites) {
					if (!raw || typeof raw !== "object") continue;
					const site = raw as Record<string, unknown>;
					if (typeof site.siteId !== "string") continue;
					const status = String(site.status ?? "");
					if (
						status !== "preview_ready" &&
						status !== "published" &&
						status !== "cloning" &&
						status !== "pending"
					) {
						continue;
					}
					clones.push({
						siteId: site.siteId,
						name: typeof site.name === "string" ? site.name : "Website",
						sourceUrl:
							typeof site.sourceUrl === "string" ? site.sourceUrl : "",
						phase:
							status === "preview_ready" || status === "published"
								? "ready"
								: siteClonePhase(site.phase),
						status,
						progress:
							typeof site.progress === "number" ? site.progress : 0,
						plannedPageCount:
							typeof site.plannedPageCount === "number"
								? site.plannedPageCount
								: null,
						pagesCompleted:
							typeof site.pagesCompleted === "number"
								? site.pagesCompleted
								: null,
						etaRemainingSec:
							typeof site.etaRemainingSec === "number"
								? site.etaRemainingSec
								: null,
						previewUrl:
							typeof site.previewUrl === "string" ? site.previewUrl : null,
						publishedUrl:
							typeof site.publishedUrl === "string"
								? site.publishedUrl
								: null,
						lastError:
							typeof site.lastError === "string" ? site.lastError : null,
					});
				}
				if (clones.length === 0 || cancelled) return;

				setMairkoThreads((prev) => {
					let any = false;
					const nextThreads = prev.map((thread) => {
						const stripped = thread.messages.filter(
							(row) => !isInjectedSiteCard(row),
						);
						let changed = stripped.length !== thread.messages.length;
						const messages = stripped.map((message) => {
							if (!message.siteClones?.length) return message;
							let localTouch = false;
							const siteClones = message.siteClones.map((c) => {
								const next = clones.find((row) => row.siteId === c.siteId);
								if (!next) return c;
								localTouch = true;
								const wasReady =
									c.status === "preview_ready" || c.phase === "ready";
								const nowReady =
									next.status === "preview_ready" || next.phase === "ready";
								if (nowReady && !wasReady) {
									pingWorkingCopyReady(
										next.siteId,
										next.name,
										next.previewUrl,
										thread.id,
									);
								}
								return { ...c, ...next };
							});
							if (!localTouch) return message;
							changed = true;
							return { ...message, siteClones };
						});
						if (!changed) return thread;
						any = true;
						return { ...thread, messages };
					});
					return any ? nextThreads : prev;
				});
			} catch {
				/* ignore */
			}
		})();
		return () => {
			cancelled = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps -- hydrate on focus / setup
	}, [activeMairkoThreadId, setupDone, phase]);

	useEffect(() => {
		const onKey = (event: KeyboardEvent) => {
			const mod = event.metaKey || event.ctrlKey;
			if (!mod || event.altKey || event.shiftKey) return;
			if (event.key.toLowerCase() !== "b") return;
			event.preventDefault();
			setLeftOpen((open) => !open);
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, []);

	const agent = agentById(agentId);
	const messages = messagesByAgent[agentId] ?? [];

	const appendMessages = (agentKey: AgentId, next: ChatMessage[]) => {
		setMessagesByAgent((prev) => ({
			...prev,
			[agentKey]: [...(prev[agentKey] ?? []), ...next],
		}));
	};

	useEffect(() => {
		if (setupDone) return;
		if (mairkoThreads.some(threadUnlocksDesk)) setSetupDone(true);
	}, [mairkoThreads, setupDone]);

	/** Desk unlocks after paywall / a working copy — same thread continues. */
	useEffect(() => {
		if (!sessionReady) return;
		let cancelled = false;
		void (async () => {
			const status = await fetchOnboardingStatus();
			if (cancelled) return;
			if (!status.ok) {
				setSetupKnown(true);
				return;
			}
			if (status.workspaceName) setTenantName(status.workspaceName);
			const latest = status.latest;
			const paidLocally = readMairkoThreads().some(threadUnlocksDesk);
			if (
				status.deskReady ||
				(status.complete && workingCopyEntitled) ||
				paidLocally
			) {
				setSetupDone(true);
				if (latest) {
					setSession((prev) =>
						completeOnboardingSession(prev, {
							company: latest.card?.brandName ?? prev.profile.company ?? "",
							website: latest.siteUrl,
						}),
					);
				}
				setPhase("app");
				setSetupKnown(true);
				return;
			}

			const stub = readOnboardingSession();
			if (stub && isOnboardingComplete(stub) && !status.complete) {
				clearOnboardingSession();
				setSession(emptySession());
				const seed = resetMairkoThreads();
				setMairkoThreads(seed);
				setActiveMairkoThreadId(seed[0]!.id);
				setMessagesByAgent((prev) => ({
					...prev,
					onboarding: welcomeMessages(),
				}));
			}

			setSetupDone(false);
			setPhase("app");
			setWorkspace("chat");
			setChatSurface("messages");
			setMairkoThreads((prev) => {
				const inFlight = prev.find(threadHasResumeWork);
				if (inFlight) {
					setActiveMairkoThreadId(inFlight.id);
					return [inFlight];
				}
				const seeded = seedMainChatOnboarding({
					latest,
				});
				const base = prev[0] ?? createMairkoThread();
				setActiveMairkoThreadId(base.id);
				setSitePreviewUrl(null);
				return [
					{
						...base,
						title: "New chat",
						messages: seeded,
						updatedAt: new Date().toISOString(),
					},
				];
			});
			if (!latest) {
				setSetupKnown(true);
				return;
			}
			if (latest.status === "confirmed") {
				setSession((prev) =>
					completeOnboardingSession(prev, {
						company: latest.card?.brandName ?? prev.profile.company ?? "",
						website: latest.siteUrl,
					}),
				);
			} else if (latest.status === "ready") {
				setSession((prev) => ({
					...prev,
					step: "findings",
					profile: {
						...prev.profile,
						website: latest.siteUrl,
						packId: latest.packId,
						company: latest.card?.brandName ?? prev.profile.company,
					},
				}));
			} else if (
				latest.status === "researching" ||
				latest.status === "pending"
			) {
				setSession((prev) => ({
					...prev,
					step: "researching",
					profile: {
						...prev.profile,
						website: latest.siteUrl,
						packId: latest.packId,
					},
				}));
			}
			setSetupKnown(true);
		})();
		return () => {
			cancelled = true;
		};
		// workingCopyEntitled is read on this run only — do not re-seed when
		// has() flips after checkout; onWorkingCopyPaid unlocks the desk.
	}, [sessionReady, setTenantName]);

	const applyAdvance = (result: {
		session: OnboardingSession;
		messages: ChatMessage[];
	}) => {
		setSession(result.session);
		if (result.messages.length === 0) return;
		setTyping(true);
		window.setTimeout(() => {
			appendMessages("onboarding", result.messages);
			setTyping(false);
		}, 420);
	};

	const syncSessionFromResearch = (rows: ChatCompanyResearch[]) => {
		const latest = rows[rows.length - 1];
		if (!latest) return;
		if (latest.status === "confirmed") {
			const name = latest.card?.brandName ?? latest.brandName;
			if (name) setTenantName(name);
		}
		setSession((prev) => {
			if (latest.status === "confirmed") {
				return completeOnboardingSession(prev, {
					company: latest.card?.brandName ?? latest.brandName ?? "",
					website: latest.siteUrl,
				});
			}
			if (latest.status === "ready") {
				return {
					...prev,
					step: "findings",
					profile: {
						...prev.profile,
						website: latest.siteUrl,
						packId: latest.packId,
						company: latest.card?.brandName ?? latest.brandName,
					},
				};
			}
			return {
				...prev,
				step: "researching",
				profile: {
					...prev.profile,
					website: latest.siteUrl,
					packId: latest.packId,
				},
			};
		});
	};

	const goNav = (next: PrimaryNav) => {
		if (!setupDone && next !== "agents") return;
		setSettingsOpen(false);
		setWorkspace("desk");
		setNav(next);
		setSelectedIntegration(null);
		if (next === "agents") {
			if (!setupDone) {
				setAgentId("onboarding");
				setAgentsSurface("chat");
			} else {
				setAgentsSurface("hub");
			}
		}
	};

	const openSettings = (tab: SettingsTab = "account") => {
		setSettingsTab(tab);
		setSettingsOpen(true);
	};

	const openMairkoChat = () => {
		setSettingsOpen(false);
		setWorkspace("chat");
		setSelectedIntegration(null);
	};

	const newMairkoChat = () => {
		if (!setupDone) return;
		const thread = createMairkoThread();
		setMairkoThreads((prev) => [thread, ...prev]);
		setActiveMairkoThreadId(thread.id);
		setMairkoDraft("");
		setSitePreviewUrl(null);
		setWorkspace("chat");
	};

	const pinMairkoThread = (id: string) => {
		setMairkoThreads((prev) => togglePinMairkoThread(prev, id));
	};

	const renameMairkoChat = (id: string, title: string) => {
		setMairkoThreads((prev) => renameMairkoThread(prev, id, title));
	};

	const deleteMairkoChat = (id: string) => {
		const result = deleteMairkoThread(mairkoThreads, id);
		setMairkoThreads(result.threads);
		if (activeMairkoThreadId === id && result.nextActiveId) {
			setActiveMairkoThreadId(result.nextActiveId);
			setMairkoDraft("");
		}
	};

	const openAgentChat = (id: AgentId) => {
		if (!setupDone) return;
		setSelectedIntegration(null);
		setAgentId(id);
		setAgentsSurface("chat");
		setNav("agents");
		setWorkspace("desk");
	};

	const openIntegration = (
		item: Integration,
		kind: "connector" | "channel",
	) => {
		if (!setupDone) return;
		const live =
			kind === "connector"
				? (connections.connectors.find((row) => row.id === item.id) ?? item)
				: (connections.channels.find((row) => row.id === item.id) ?? item);
		// Stay in the chat shell when opening catalogs from chat / palette.
		if (workspace === "chat") {
			setChatSurface(kind === "connector" ? "connectors" : "channels");
			setSelectedIntegration(live);
			return;
		}
		setWorkspace("desk");
		setNav(kind === "connector" ? "connectors" : "channels");
		setSelectedIntegration(live);
	};

	const backToAgentsHub = () => {
		setWorkspace("desk");
		setNav("agents");
		setAgentsSurface("hub");
		setSelectedIntegration(null);
	};

	const toolbarProps = {
		leftOpen,
		onToggleLeft: () => setLeftOpen((o) => !o),
		workspace,
		onWorkspaceChange: (mode: WorkspaceMode) => {
			if (!setupDone) return;
			setWorkspace(mode);
		},
		nav,
		setupDone,
		onNavChange: goNav,
		onOpenSearch: () => {
			if (!setupDone) return;
			setSearchOpen(true);
		},
		onOpenChat: openMairkoChat,
		onNewChat: newMairkoChat,
		onAskAgent: openAgentChat,
	} as const;

	/** Explicit About control — kit / job modal. */
	const openAgentInfo = (id: AgentId) => {
		if (!setupDone && id !== "onboarding") return;
		setInfoAgentId(id);
	};

	const cycleTheme = () => {
		setThemePref((prev) => nextThemePreference(prev));
	};

	const confirmPack = async (packId: string) => {
		const thread =
			mairkoThreads.find((row) => row.id === activeMairkoThreadId) ??
			null;
		const existing = thread?.messages
			.flatMap((message) => message.companyResearch ?? [])
			.find((row) => row.packId === packId);
		const brandName =
			existing?.card?.brandName?.trim() ||
			existing?.brandName?.trim() ||
			"your brand";
		const siteUrl = existing?.siteUrl ?? "";
		const nextAsk = `I can help with the site and marketing. Ready for us to design ${brandName} a new site so we can keep going?`;
		const patch: Partial<ChatCompanyResearch> = {
			status: "confirmed",
			phase: "ready",
			progress: 100,
			brandName,
		};
		const askId = crypto.randomUUID();
		setConfirmingPackId(packId);
		setMairkoThreads((prev) =>
			prev.map((row) => {
				if (row.id !== activeMairkoThreadId) return row;
				const alreadyAsked = row.messages.some(
					(message) =>
						message.workingCopyAsk && !message.workingCopyAsk.decision,
				);
				const messages = row.messages.map((message) => {
					if (!message.companyResearch?.some((item) => item.packId === packId)) {
						return message;
					}
					return {
						...message,
						companyResearch: message.companyResearch.map((item) =>
							item.packId === packId ? { ...item, ...patch } : item,
						),
					};
				});
				return {
					...row,
					title: brandName === "your brand" ? row.title : brandName,
					messages: alreadyAsked
						? messages
						: [
								...messages,
								{
									id: askId,
									agentId: "mairko",
									author: "agent" as const,
									authorName: MAIRKO_CHAT.name,
									body: nextAsk,
									at: new Date().toISOString(),
									workingCopyAsk: { brandName, siteUrl },
								},
							],
				};
			}),
		);
		const result = await confirmOnboardingPack(packId);
		setConfirmingPackId(null);
		if (!result.ok) {
			setMairkoThreads((prev) =>
				prev.map((row) => {
					if (row.id !== activeMairkoThreadId) return row;
					return {
						...row,
						messages: [
							...row.messages
								.filter((message) => message.id !== askId)
								.map((message) => {
									if (
										!message.companyResearch?.some(
											(item) => item.packId === packId,
										)
									) {
										return message;
									}
									return {
										...message,
										companyResearch: message.companyResearch.map((item) =>
											item.packId === packId
												? {
														...item,
														status: "ready" as const,
														phase: "ready" as const,
													}
												: item,
										),
									};
								}),
							{
								id: crypto.randomUUID(),
								agentId: "mairko",
								author: "agent" as const,
								authorName: MAIRKO_CHAT.name,
								body:
									result.message ??
									"I couldn’t confirm that pack yet. Try again in a moment.",
								at: new Date().toISOString(),
							},
						],
					};
				}),
			);
			return;
		}
		setTenantName(result.brandName);
		setSession((prev) =>
			completeOnboardingSession(prev, {
				company: result.brandName,
				website: result.siteUrl,
			}),
		);
		setMessagesByAgent((prev) => ({
			...prev,
			onboarding: (prev.onboarding ?? []).map((message) => {
				if (!message.companyResearch?.some((row) => row.packId === packId)) {
					return message;
				}
				return {
					...message,
					companyResearch: message.companyResearch.map((row) =>
						row.packId === packId
							? { ...row, ...patch, brandName: result.brandName }
							: row,
					),
				};
			}),
		}));
		setMairkoThreads((prev) =>
			prev.map((row) => ({
				...row,
				title:
					row.id === activeMairkoThreadId
						? result.brandName || row.title
						: row.title,
				messages: row.messages.map((message) => {
					if (message.id === askId && message.workingCopyAsk) {
						return {
							...message,
							body: result.nextAsk,
							workingCopyAsk: {
								brandName: result.brandName,
								siteUrl: result.siteUrl,
							},
						};
					}
					if (!message.companyResearch?.some((item) => item.packId === packId)) {
						return message;
					}
					return {
						...message,
						companyResearch: message.companyResearch.map((item) =>
							item.packId === packId
								? { ...item, ...patch, brandName: result.brandName }
								: item,
						),
					};
				}),
			})),
		);
	};

	const patchWorkingCopyAsk = (
		messageId: string,
		decision: "yes" | "later" | undefined,
	) => {
		setMairkoThreads((prev) =>
			prev.map((thread) => ({
				...thread,
				messages: thread.messages.map((message) => {
					if (message.id !== messageId || !message.workingCopyAsk) {
						return message;
					}
					const { decision: _prior, ...rest } = message.workingCopyAsk;
					return {
						...message,
						workingCopyAsk: decision ? { ...rest, decision } : rest,
					};
				}),
			})),
		);
	};

	const appendWorkingCopyNote = (
		threadId: string,
		body: string,
		designs?: ChatSiteDesign[],
	) => {
		const note: ChatMessage = {
			id: crypto.randomUUID(),
			agentId: "mairko",
			author: "agent",
			authorName: MAIRKO_CHAT.name,
			body,
			at: new Date().toISOString(),
			siteDesigns: designs,
		};
		setMairkoThreads((prev) => {
			const current = prev.find((t) => t.id === threadId);
			if (!current) return prev;
			return upsertMairkoThread(prev, {
				...current,
				messages: [...current.messages, note],
			});
		});
	};

	const startWorkingCopy = async (
		ask: ChatWorkingCopyAsk,
		messageId: string,
		opts?: { retryPaywall?: boolean; alreadyNoted?: boolean },
	): Promise<"started" | "paywall" | "failed"> => {
		const thread =
			mairkoThreads.find((row) => row.id === activeMairkoThreadId)
				?.messages ?? [];
		const url = resolveWorkingCopyUrl({
			askUrl: ask.siteUrl,
			packUrl: brandFromThread(thread)?.siteUrl,
			profileUrl: session.profile.website,
			messages: thread,
		});
		if (!url) {
			appendWorkingCopyNote(
				activeMairkoThreadId,
				"Need the current website to learn from — paste the URL and hit Yes again.",
			);
			return "failed";
		}

		if (workingCopyEntitled && !clerkOrgId && organization?.id) {
			await ensurePlatformSession({
				getToken: () =>
					getToken({ organizationId: organization.id }),
				orgId: organization.id,
				orgName: organization.name,
				timeoutMs: 8_000,
			});
		}

		const result = await startNewSiteBuild(url);
		if (result.ok) {
			patchWorkingCopyAsk(messageId, "yes");
			setSetupDone(true);
			appendWorkingCopyNote(
				activeMairkoThreadId,
				opts?.alreadyNoted
					? ""
					: `Designing ${ask.brandName} a new site. Open Code to watch it land — I’ll ping you when the preview is ready.`,
				[
					{
						siteId: result.siteId,
						name: result.name,
						sourceUrl: result.sourceUrl,
						phase: "started",
						status: result.status,
						progress: result.progress,
					},
				],
			);
			return "started";
		}
		if (result.error === "payment_required") {
			if (workingCopyEntitled) {
				appendWorkingCopyNote(
					activeMairkoThreadId,
					"This workspace is Pro, but I couldn’t confirm billing on the server yet. Stay here — try Yes again in a moment.",
				);
				return "failed";
			}
			if (opts?.retryPaywall) {
				await new Promise((resolve) => window.setTimeout(resolve, 1500));
				return startWorkingCopy(ask, messageId);
			}
			return "paywall";
		}
		appendWorkingCopyNote(
			activeMairkoThreadId,
			"I couldn’t start the new site just now. Stay here — we can keep going from what I already learned.",
		);
		return "failed";
	};

	const onWorkingCopyYes = async (
		ask: ChatWorkingCopyAsk,
		messageId: string,
	) => {
		patchWorkingCopyAsk(messageId, "yes");
		const outcome = await startWorkingCopy(ask, messageId);
		if (outcome === "paywall") {
			patchWorkingCopyAsk(messageId, undefined);
			setWorkingCopyCheckout({ messageId, ask });
			return;
		}
		if (outcome === "failed") {
			patchWorkingCopyAsk(messageId, undefined);
		}
	};

	const onWorkingCopyPaid = async () => {
		if (!workingCopyCheckout) return;
		const { messageId, ask } = workingCopyCheckout;
		const outcome = await startWorkingCopy(ask, messageId, {
			retryPaywall: true,
		});
		if (outcome !== "paywall") setWorkingCopyCheckout(null);
	};

	const onWorkingCopyLater = (messageId: string) => {
		patchWorkingCopyAsk(messageId, "later");
		setWorkingCopyCheckout(null);
	};

	const onCardAction = (action: CardAction) => {
		switch (action) {
			case "start": {
				applyAdvance(startOnboarding(session));
				return;
			}
			case "accept_findings": {
				const packId = session.profile.packId;
				if (packId) void confirmPack(packId);
				return;
			}
			case "open_connectors": {
				setWorkspace("chat");
				setChatSurface("connectors");
				setSelectedIntegration(null);
				return;
			}
			case "open_channels": {
				setWorkspace("chat");
				setChatSurface("channels");
				setSelectedIntegration(null);
				return;
			}
			default: {
				const _exhaustive: never = action;
				return _exhaustive;
			}
		}
	};

	const sendText = (text: string) => {
		const trimmed = text.trim();
		if (!trimmed || typing) return;

		const human: ChatMessage = {
			id: crypto.randomUUID(),
			agentId,
			author: "human",
			authorName: displayName(session),
			body: trimmed,
			at: new Date().toISOString(),
		};
		const pendingId = crypto.randomUUID();
		const pending: ChatMessage = {
			id: pendingId,
			agentId,
			author: "agent",
			authorName: agent.name,
			avatar: agent.avatar,
			body: "",
			pending: true,
			at: new Date().toISOString(),
		};
		const prior = messagesByAgent[agentId] ?? [];
		setMessagesByAgent((prev) => ({
			...prev,
			[agentId]: [...(prev[agentId] ?? []), human, pending],
		}));
		setDraft("");
		setTyping(true);

		const history = [...prior, human]
			.filter((row) => row.author === "human" || row.author === "agent")
			.filter((row) => row.body.trim().length > 0)
			.slice(-20)
			.map((row) => ({
				role:
					row.author === "human"
						? ("user" as const)
						: ("assistant" as const),
				content: row.body,
			}));

		void (async () => {
			const result = await streamAgentChat({
				agentId,
				messages: history,
				onDelta: ({
					reasoning,
					text,
					handoffs,
					connects,
					siteClones,
					siteDesigns,
					companyResearch,
				}) => {
					if (companyResearch.length > 0) {
						syncSessionFromResearch(companyResearch);
					}
					setMessagesByAgent((prev) => {
						const list = prev[agentId] ?? [];
						return {
							...prev,
							[agentId]: list.map((row) =>
								row.id === pendingId
									? {
											...row,
											body: text,
											reasoning: reasoning || undefined,
											handoffs:
												handoffs.length > 0
													? handoffs
													: row.handoffs,
											connects:
												connects.length > 0
													? connects
													: row.connects,
											siteClones: mergeSiteCloneLists(
												row.siteClones,
												siteClones,
											),
											siteDesigns: mergeSiteDesignLists(
												row.siteDesigns,
												siteDesigns,
											),
											companyResearch:
												companyResearch.length > 0
													? companyResearch
													: row.companyResearch,
										}
									: row,
							),
						};
					});
				},
			});
			setTyping(false);
			if (result.ok && result.text.trim()) {
				void persistDeskChatTurn({
					agentId,
					threadId: agentId,
					content: result.text,
				});
			}
			setMessagesByAgent((prev) => {
				const list = prev[agentId] ?? [];
				return {
					...prev,
					[agentId]: list.map((row) => {
						if (row.id !== pendingId) return row;
						if (!result.ok) {
							const detail =
								result.error === "llm_not_configured"
									? "Chat isn’t available right now. Your message is saved — try again shortly."
									: result.error === "aborted"
										? row.body || "Stopped."
										: `I couldn’t reach the model (${result.error}). Try again in a moment.`;
							return {
								...row,
								body: row.body.trim() ? row.body : detail,
								pending: false,
								at: new Date().toISOString(),
							};
						}
						return {
							...row,
							body: result.text,
							reasoning: result.reasoning ?? row.reasoning,
							handoffs:
								result.handoffs.length > 0
									? result.handoffs
									: row.handoffs,
							connects:
								result.connects.length > 0
									? result.connects
									: row.connects,
							siteClones: mergeSiteCloneLists(
								row.siteClones,
								result.siteClones,
							),
							siteDesigns: mergeSiteDesignLists(
								row.siteDesigns,
								result.siteDesigns,
							),
							companyResearch:
								result.ok && result.companyResearch.length > 0
									? result.companyResearch
									: row.companyResearch,
							pending: false,
							at: new Date().toISOString(),
						};
					}),
				};
			});
		})();
	};

	const sendMairkoText = (
		text: string,
		meta?: {
			modelId?: string;
			plugins?: string[];
			files?: Array<{
				mediaType?: string;
				url?: string;
				filename?: string;
			}>;
		},
	) => {
		const trimmed = text.trim();
		if ((!trimmed && !(meta?.files?.length)) || mairkoTyping) return;
		const pendingAsk = findPendingWorkingCopyAsk(
			mairkoThreads.find((t) => t.id === activeMairkoThreadId)?.messages ??
				[],
		);
		if (
			trimmed &&
			!meta?.files?.length &&
			pendingAsk &&
			/^(yes|yeah|yep|yup|ok|okay|sure|good|let'?s (go|do it)|do it)\b/i.test(
				trimmed,
			)
		) {
			void onWorkingCopyYes(pendingAsk.ask, pendingAsk.messageId);
			return;
		}

		const threadId = activeMairkoThreadId;
		const publishCmd =
			trimmed && !meta?.files?.length
				? parseHostedPublishCommand(trimmed)
				: null;
		if (publishCmd) {
			void (async () => {
				const human: ChatMessage = {
					id: crypto.randomUUID(),
					agentId: "mairko",
					author: "human",
					authorName: displayName(session),
					body: trimmed,
					at: new Date().toISOString(),
				};
				const prior =
					mairkoThreads.find((t) => t.id === threadId)?.messages ?? [];
				setMairkoThreads((prev) => {
					const current = prev.find((t) => t.id === threadId);
					if (!current) return prev;
					return upsertMairkoThread(prev, {
						...current,
						messages: [...current.messages, human],
					});
				});
				setMairkoDraft("");
				try {
					const response = await fetch(
						`/api/sites/${publishCmd.siteId}/status?attach=1`,
						{ credentials: "include" },
					);
					const json: unknown = await response.json();
					const site =
						json &&
						typeof json === "object" &&
						"site" in json &&
						json.site &&
						typeof json.site === "object"
							? (json.site as Record<string, unknown>)
							: null;
					if (!site || !response.ok) {
						appendWorkingCopyNote(
							threadId,
							"Couldn’t reach the site to publish. Try again in a moment.",
						);
						return;
					}
					const prevClone =
						collectSiteClones(prior).find(
							(row) => row.siteId === publishCmd.siteId,
						) ?? {
							siteId: publishCmd.siteId,
							name: publishCmd.siteName,
							sourceUrl: "",
							phase: "ready" as const,
							status: "preview_ready",
							progress: 100,
						};
					patchMairkoSiteClone(
						publishCmd.siteId,
						patchFromSiteStatus(site, prevClone),
					);
					const live =
						typeof site.publishedUrl === "string"
							? site.publishedUrl
							: "";
					const err =
						typeof site.lastError === "string" ? site.lastError : "";
					if (live && !live.includes("workers.dev")) {
						appendWorkingCopyNote(
							threadId,
							`Live at ${live}`,
						);
						return;
					}
					if (String(site.status) === "published") {
						appendWorkingCopyNote(
							threadId,
							err
								? `Still on the temporary address. ${err}`
								: live
									? `Still live at ${live} — the Mairko address didn’t attach.`
									: "The site is published, but the Mairko address didn’t attach.",
						);
						return;
					}
				} catch {
					appendWorkingCopyNote(
						threadId,
						"Couldn’t reach the site to publish. Try again in a moment.",
					);
					return;
				}
				await streamMairkoReply({
					threadId,
					baseMessages: [...prior, human],
					modelId: meta?.modelId,
				});
			})();
			return;
		}
		const priorForClone =
			mairkoThreads.find((t) => t.id === threadId)?.messages ?? [];
		const cloneCmd =
			trimmed && !meta?.files?.length
				? parseWorkingCopyCommand(trimmed)
				: null;
		if (cloneCmd) {
			const brand = brandFromThread(priorForClone);
			const fromPrior = [...priorForClone]
				.reverse()
				.map((row) =>
					row.author === "human" ? extractOnboardingUrl(row.body) : null,
				)
				.find((href): href is string => Boolean(href));
			const url =
				cloneCmd.url ??
				brand?.siteUrl ??
				fromPrior ??
				session.profile.website?.trim() ??
				"";
			if (url) {
				const host = (() => {
					try {
						return new URL(url).hostname.replace(/^www\./i, "");
					} catch {
						return url;
					}
				})();
				const ask: ChatWorkingCopyAsk = {
					brandName: brand?.brandName ?? host,
					siteUrl: url,
				};
				const human: ChatMessage = {
					id: crypto.randomUUID(),
					agentId: "mairko",
					author: "human",
					authorName: displayName(session),
					body: trimmed,
					at: new Date().toISOString(),
				};
				setMairkoThreads((prev) => {
					const current = prev.find((t) => t.id === threadId);
					if (!current) return prev;
					return upsertMairkoThread(prev, {
						...current,
						messages: [...current.messages, human],
					});
				});
				appendWorkingCopyNote(
					threadId,
					`Designing ${ask.brandName} a new site. Open Code to watch it land — I’ll ping you when the preview is ready.`,
				);
				void (async () => {
					const outcome = await startWorkingCopy(ask, human.id, {
						alreadyNoted: true,
					});
					if (outcome === "paywall") {
						appendWorkingCopyNote(
							threadId,
							"Need a plan on this workspace first — opening billing.",
						);
						setWorkingCopyCheckout({ messageId: human.id, ask });
					}
				})();
				setMairkoDraft("");
				return;
			}
		}
		const prior =
			mairkoThreads.find((t) => t.id === threadId)?.messages ?? [];

		void (async () => {
			const files = await normalizeChatFiles(meta?.files ?? []);
			if (!trimmed && files.length === 0) return;

			const human: ChatMessage = {
				id: crypto.randomUUID(),
				agentId: "mairko",
				author: "human",
				authorName: displayName(session),
				body: trimmed || "What do you see in this attachment?",
				at: new Date().toISOString(),
				attachments: files.map((file, index) => ({
					id: `${crypto.randomUUID()}-${index}`,
					filename: file.filename,
					mediaType: file.mediaType,
					url: file.url,
				})),
			};

			await streamMairkoReply({
				threadId,
				baseMessages: [...prior, human],
				attachments: files,
				modelId: meta?.modelId,
			});
			setMairkoDraft("");
			if (!setupDone) {
				const siteUrl = extractOnboardingUrl(trimmed);
				if (siteUrl) {
					setSession((prev) => ({
						...prev,
						step: "researching",
						profile: { ...prev.profile, website: siteUrl },
					}));
				}
			}
		})();
	};

	/** Truncate after a human turn and re-stream the assistant reply. */
	const streamMairkoReply = async (args: {
		threadId: string;
		baseMessages: ChatMessage[];
		attachments?: Array<{
			mediaType: string;
			url: string;
			filename?: string;
		}>;
		modelId?: string;
		/** When set, used as the LLM history instead of deriving from baseMessages. */
		historyOverride?: Array<{ role: "user" | "assistant"; content: string }>;
	}) => {
		const { threadId, baseMessages, attachments, historyOverride } =
			args;
		const modelId = args.modelId ?? readStoredModel();
		const pendingId = crypto.randomUUID();
		const inheritedClones = collectSiteClones(baseMessages).filter(
			siteCloneInFlight,
		);
		const pending: ChatMessage = {
			id: pendingId,
			agentId: "mairko",
			author: "agent",
			authorName: MAIRKO_CHAT.name,
			body: "",
			pending: true,
			at: new Date().toISOString(),
			siteClones:
				inheritedClones.length > 0 ? inheritedClones : undefined,
		};

		setMairkoThreads((prev) => {
			const current = prev.find((t) => t.id === threadId);
			if (!current) {
				return upsertMairkoThread(prev, {
					id: threadId,
					title: "New chat",
					updatedAt: new Date().toISOString(),
					messages: [...baseMessages, pending],
				});
			}
			return upsertMairkoThread(prev, {
				...current,
				messages: [...baseMessages, pending],
			});
		});

		const history =
			historyOverride ??
			baseMessages
				.filter((row) => row.author === "human" || row.author === "agent")
				.filter((row) => row.body.trim().length > 0)
				.slice(-24)
				.map((row) => ({
					role:
						row.author === "human"
							? ("user" as const)
							: ("assistant" as const),
					content: row.body,
				}));

		const lastHuman = [...baseMessages]
			.reverse()
			.find((row) => row.author === "human");
		const fileParts =
			attachments ??
			(lastHuman?.attachments ?? []).flatMap((att) => {
				if (
					typeof att.mediaType !== "string" ||
					typeof att.url !== "string" ||
					!att.url.startsWith("data:")
				) {
					return [];
				}
				return [
					{
						mediaType: att.mediaType,
						url: att.url,
						filename: att.filename,
					},
				];
			});

		const result = await streamAgentChat({
			agentId: "mairko",
			threadId,
			messages: history,
			modelId,
			attachments: fileParts,
			onDelta: ({
				reasoning,
				text: deltaText,
				handoffs,
				connects,
				siteClones,
				siteDesigns,
				companyResearch,
			}) => {
				if (companyResearch.length > 0) {
					syncSessionFromResearch(companyResearch);
				}
				const preview =
					previewUrlFromHandoffs(handoffs) ??
					previewUrlFromSiteClones(siteClones);
				if (preview) setSitePreviewUrl(preview);
				setMairkoThreads((prev) => {
					const current = prev.find((t) => t.id === threadId);
					if (!current) return prev;
					return upsertMairkoThread(prev, {
						...current,
						messages: current.messages.map((row) =>
							row.id === pendingId
								? {
										...row,
										body: deltaText,
										reasoning: reasoning || undefined,
										handoffs:
											handoffs.length > 0 ? handoffs : row.handoffs,
										connects:
											connects.length > 0 ? connects : row.connects,
										siteClones: mergeSiteCloneLists(
											row.siteClones,
											siteClones,
										),
										siteDesigns: mergeSiteDesignLists(
											row.siteDesigns,
											siteDesigns,
										),
										companyResearch:
											companyResearch.length > 0
												? companyResearch
												: row.companyResearch,
									}
								: row,
						),
					});
				});
			},
		});
		if (result.ok) {
			const preview =
				previewUrlFromHandoffs(result.handoffs) ??
				previewUrlFromSiteClones(result.siteClones);
			if (preview) setSitePreviewUrl(preview);
			// Backup persist: stream tee can still miss under Worker lifetime.
			if (result.text.trim()) {
				void persistDeskChatTurn({
					agentId: "mairko",
					threadId,
					content: result.text,
				});
			}
		}

		let needsSidebarTitle = false;
		setMairkoThreads((prev) => {
			const current = prev.find((t) => t.id === threadId);
			if (!current) return prev;
			if (
				result.ok &&
				!current.titleLocked &&
				!current.titleGenerated
			) {
				needsSidebarTitle = true;
			}
			return upsertMairkoThread(prev, {
				...current,
				messages: current.messages.map((row) => {
					if (row.id !== pendingId) return row;
					if (!result.ok) {
						const detail =
							result.error === "llm_not_configured"
								? "Chat isn’t available right now. Your message is saved — try again shortly."
								: result.error === "aborted"
									? row.body || "Stopped."
									: `I couldn’t reach the model (${result.error}). Try again in a moment.`;
						return {
							...row,
							body: row.body.trim() ? row.body : detail,
							pending: false,
							at: new Date().toISOString(),
						};
					}
					return {
						...row,
						body: result.text,
						reasoning: result.reasoning ?? row.reasoning,
						handoffs:
							result.handoffs.length > 0
								? result.handoffs
								: row.handoffs,
						connects:
							result.connects.length > 0
								? result.connects
								: row.connects,
						siteClones: mergeSiteCloneLists(
							row.siteClones,
							result.siteClones,
						),
						siteDesigns: mergeSiteDesignLists(
							row.siteDesigns,
							result.siteDesigns,
						),
						companyResearch:
							result.companyResearch.length > 0
								? result.companyResearch
								: row.companyResearch,
						pending: false,
						at: new Date().toISOString(),
					};
				}),
			});
		});

		// Claude-style Recents: topical title after the first successful reply.
		const isFirstAssistantReply =
			baseMessages.filter(
				(m) => m.author === "agent" && m.body.trim().length > 0,
			).length === 0;
		if (result.ok && needsSidebarTitle && isFirstAssistantReply) {
			const firstHuman = baseMessages.find((m) => m.author === "human");
			const userMessage = firstHuman?.body.trim() ?? "";
			if (userMessage) {
				void requestChatTitle({
					userMessage,
					assistantMessage: result.text,
				}).then((titled) => {
					if (!titled.ok) return;
					setMairkoThreads((prev) =>
						applyGeneratedMairkoTitle(prev, threadId, titled.title),
					);
				});
			}
		}
	};

	const retryMairkoMessage = (messageId: string) => {
		if (mairkoTyping) return;
		const threadId = activeMairkoThreadId;
		const thread = mairkoThreads.find((t) => t.id === threadId);
		if (!thread) return;
		const idx = thread.messages.findIndex((m) => m.id === messageId);
		if (idx < 0) return;

		const target = thread.messages[idx];
		let humanIdx = idx;
		if (target?.author === "agent") {
			for (let i = idx - 1; i >= 0; i -= 1) {
				if (thread.messages[i]?.author === "human") {
					humanIdx = i;
					break;
				}
			}
		}
		const human = thread.messages[humanIdx];
		if (!human || human.author !== "human") return;

		const baseMessages = thread.messages.slice(0, humanIdx + 1);

		void streamMairkoReply({
			threadId,
			baseMessages,
		});
	};

	const editMairkoMessage = (messageId: string, body: string) => {
		if (mairkoTyping) return;
		const trimmed = body.trim();
		if (!trimmed) return;
		const threadId = activeMairkoThreadId;
		const thread = mairkoThreads.find((t) => t.id === threadId);
		if (!thread) return;
		const idx = thread.messages.findIndex((m) => m.id === messageId);
		const human = thread.messages[idx];
		if (!human || human.author !== "human") return;

		const baseMessages = [
			...thread.messages.slice(0, idx),
			{
				...human,
				body: trimmed,
				at: new Date().toISOString(),
			},
		];

		void streamMairkoReply({
			threadId,
			baseMessages,
		});
	};

	const patchMairkoHandoff = (
		handoffId: string,
		patch: Partial<ChatHandoff>,
	) => {
		setMairkoThreads((prev) =>
			prev.map((thread) => {
				if (thread.id !== activeMairkoThreadId) return thread;
				let touched = false;
				const messages = thread.messages.map((message) => {
					if (!message.handoffs?.some((h) => h.handoffId === handoffId)) {
						return message;
					}
					touched = true;
					return {
						...message,
						handoffs: message.handoffs.map((h) =>
							h.handoffId === handoffId ? { ...h, ...patch } : h,
						),
					};
				});
				return touched ? { ...thread, messages } : thread;
			}),
		);
	};

	const patchMairkoSiteClone = (
		siteId: string,
		patch: Partial<ChatSiteClone>,
	) => {
		if (
			(patch.status === "preview_ready" || patch.phase === "ready") &&
			typeof patch.previewUrl === "string" &&
			patch.previewUrl
		) {
			setSitePreviewUrl(patch.previewUrl);
		}
		let becameReady = false;
		let readyName = "your site";
		setMairkoThreads((prev) =>
			prev.map((thread) => {
				let touched = false;
				const messages = thread.messages.map((message) => {
					if (!message.siteClones?.some((c) => c.siteId === siteId)) {
						return message;
					}
					touched = true;
					return {
						...message,
						siteClones: message.siteClones.map((c) => {
							if (c.siteId !== siteId) return c;
							const wasReady =
								c.status === "preview_ready" || c.phase === "ready";
							const next = { ...c, ...patch };
							const nowReady =
								next.status === "preview_ready" || next.phase === "ready";
							if (nowReady && !wasReady) {
								becameReady = true;
								readyName = next.name || readyName;
							}
							return next;
						}),
					};
				});
				return touched ? { ...thread, messages } : thread;
			}),
		);
		if (becameReady) {
			pingWorkingCopyReady(siteId, readyName, patch.previewUrl);
		}
	};

	const patchMairkoSiteDesign = (
		siteId: string,
		patch: Partial<ChatSiteDesign>,
	) => {
		if (
			(patch.status === "preview_ready" || patch.phase === "ready") &&
			typeof patch.previewUrl === "string" &&
			patch.previewUrl
		) {
			setSitePreviewUrl(patch.previewUrl);
		} else if (
			(patch.lastFile ||
				patch.phase === "generating" ||
				patch.status === "building") &&
			typeof patch.previewUrl === "string" &&
			patch.previewUrl
		) {
			setSitePreviewUrl((current) => current ?? patch.previewUrl ?? null);
		}
		setMairkoThreads((prev) =>
			prev.map((thread) => {
				let touched = false;
				const messages = thread.messages.map((message) => {
					if (!message.siteDesigns?.some((row) => row.siteId === siteId)) {
						return message;
					}
					touched = true;
					return {
						...message,
						siteDesigns: message.siteDesigns.map((row) =>
							row.siteId === siteId ? { ...row, ...patch } : row,
						),
					};
				});
				return touched ? { ...thread, messages } : thread;
			}),
		);
		setMessagesByAgent((prev) => {
			let changed = false;
			const next: Record<string, ChatMessage[]> = {};
			for (const [id, messages] of Object.entries(prev)) {
				next[id] = messages.map((message) => {
					if (!message.siteDesigns?.some((row) => row.siteId === siteId)) {
						return message;
					}
					changed = true;
					return {
						...message,
						siteDesigns: message.siteDesigns.map((row) =>
							row.siteId === siteId ? { ...row, ...patch } : row,
						),
					};
				});
			}
			return changed ? next : prev;
		});
	};

	const patchMairkoCompanyResearch = (
		packId: string,
		patch: Partial<ChatCompanyResearch>,
	) => {
		if (patch.status) {
			syncSessionFromResearch([
				{
					packId,
					siteUrl: patch.siteUrl ?? "",
					phase: patch.phase ?? "progress",
					status: patch.status,
					progress: patch.progress ?? 0,
					brandName: patch.brandName,
					lastError: patch.lastError,
					card: patch.card,
				},
			]);
		}
		setMairkoThreads((prev) =>
			prev.map((thread) => {
				let touched = false;
				const messages = thread.messages.map((message) => {
					if (!message.companyResearch?.some((row) => row.packId === packId)) {
						return message;
					}
					touched = true;
					return {
						...message,
						companyResearch: message.companyResearch.map((row) =>
							row.packId === packId ? { ...row, ...patch } : row,
						),
					};
				});
				return touched ? { ...thread, messages } : thread;
			}),
		);
	};

	const patchAgentCompanyResearch = (
		packId: string,
		patch: Partial<ChatCompanyResearch>,
	) => {
		if (patch.status) {
			syncSessionFromResearch([
				{
					packId,
					siteUrl: patch.siteUrl ?? session.profile.website ?? "",
					phase: patch.phase ?? "progress",
					status: patch.status,
					progress: patch.progress ?? 0,
					brandName: patch.brandName,
					lastError: patch.lastError,
					card: patch.card,
				},
			]);
		}
		setMessagesByAgent((prev) => {
			let changed = false;
			const next: Record<string, ChatMessage[]> = {};
			for (const [id, messages] of Object.entries(prev)) {
				next[id] = messages.map((message) => {
					if (!message.companyResearch?.some((row) => row.packId === packId)) {
						return message;
					}
					changed = true;
					return {
						...message,
						companyResearch: message.companyResearch.map((row) =>
							row.packId === packId ? { ...row, ...patch } : row,
						),
					};
				});
			}
			return changed ? next : prev;
		});
	};

	/**
	 * After Approve/Reject: patch the card, then start a short reassurance turn
	 * so the human hears what happened in plain language.
	 */
	const handleMairkoHandoffDecision = (event: HandoffDecisionEvent) => {
		const threadId = activeMairkoThreadId;
		patchMairkoHandoff(event.handoffId, event.patch);

		const replace =
			event.handoff.summary?.match(
				/Replacing\s+[“"']([^”"']+)[”"']\s*→\s*[“"']([^”"']+)[”"']/i,
			) ?? null;
		const detail = replace
			? `Replacing “${replace[1]}” with “${replace[2]}”.`
			: event.handoff.summary
				? `Context from the handoff: ${event.handoff.summary.slice(0, 400)}`
				: `Task was: ${event.handoff.task}`;

		const humanBody =
			event.verdict === "approve"
				? `Approved. ${detail} Reassure me in 1–2 short sentences what happens next (live vs still running). Do not call tools or re-propose.`
				: `Rejected. ${detail} Confirm in 1–2 short sentences that nothing went live. Do not call tools or re-propose.`;

		const displayBody =
			event.verdict === "approve" ? "Approved" : "Rejected";

		const thread = mairkoThreads.find((t) => t.id === threadId);
		const prior = (thread?.messages ?? []).map((message) => {
			if (!message.handoffs?.some((h) => h.handoffId === event.handoffId)) {
				return message;
			}
			return {
				...message,
				handoffs: message.handoffs.map((h) =>
					h.handoffId === event.handoffId ? { ...h, ...event.patch } : h,
				),
			};
		});

		const human: ChatMessage = {
			id: crypto.randomUUID(),
			agentId: "mairko",
			author: "human",
			authorName: displayName(session),
			body: displayBody,
			at: new Date().toISOString(),
		};

		/**
		 * History for the model uses the richer gate prompt; the bubble shows
		 * the short “Approved” / “Rejected” label.
		 */
		void streamMairkoReply({
			threadId,
			baseMessages: [...prior, human],
			historyOverride: [
				...prior
					.filter((row) => row.author === "human" || row.author === "agent")
					.filter((row) => row.body.trim().length > 0)
					.slice(-22)
					.map((row) => ({
						role:
							row.author === "human"
								? ("user" as const)
								: ("assistant" as const),
						content: row.body,
					})),
				{ role: "user" as const, content: humanBody },
			],
		});
	};

	const handlePublishDecision = (event: PublishDecisionEvent) => {
		const threadId = activeMairkoThreadId;
		patchMairkoSiteClone(event.siteId, event.patch);

		const displayBody =
			event.verdict === "approve" ? "Approved" : "Rejected";
		const human: ChatMessage = {
			id: crypto.randomUUID(),
			agentId: "mairko",
			author: "human",
			authorName: displayName(session),
			body: displayBody,
			at: new Date().toISOString(),
		};

		setMairkoThreads((prev) => {
			const current = prev.find((t) => t.id === threadId);
			if (!current) return prev;
			return upsertMairkoThread(prev, {
				...current,
				updatedAt: human.at,
				messages: [...current.messages, human],
			});
		});
	};

	const handleSiteCloneCard = (event: SiteCloneCardEvent) => {
		const key = `${event.siteId}:${event.action}`;
		if (cloneCardFollowUp.current.has(key)) return;
		cloneCardFollowUp.current.add(key);

		const threadId = activeMairkoThreadId;
		const thread = mairkoThreads.find((t) => t.id === threadId);
		const prior = thread?.messages ?? [];
		const human: ChatMessage = {
			id: crypto.randomUUID(),
			agentId: "mairko",
			author: "human",
			authorName: displayName(session),
			body: siteCloneFollowUpLabel(event),
			at: new Date().toISOString(),
		};

		void streamMairkoReply({
			threadId,
			baseMessages: [...prior, human],
			historyOverride: [
				...prior
					.filter((row) => row.author === "human" || row.author === "agent")
					.flatMap((row) => {
						const content = chatHistoryBody(row);
						if (!content) return [];
						return [
							{
								role:
									row.author === "human"
										? ("user" as const)
										: ("assistant" as const),
								content,
							},
						];
					})
					.slice(-22),
				{ role: "user" as const, content: siteCloneFollowUpPrompt(event) },
			],
		});
	};

	const progress =
		agentId === "onboarding" && !setupDone
			? progressFor(session.step)
			: null;
	const meta =
		agentId === "onboarding" ? stepMeta(session.step) : null;
	const suggestions = suggestionsFor(agentId, setupDone);
	const canType =
		agentId === "onboarding"
			? composerEnabled(session.step) && !typing
			: !typing;
	const profileLabel =
		user?.fullName?.trim() || displayName(session);
	const profileInitial = profileLabel.slice(0, 1).toUpperCase();
	const profileImageUrl = user?.imageUrl;
	const clerkEmail =
		user?.primaryEmailAddress?.emailAddress ??
		user?.emailAddresses[0]?.emailAddress ??
		session.profile.email;
	const showAdminEntry = isMairkoAdmin(clerkEmail);

	if (!sessionReady || !setupKnown) {
		return (
			<div className="app-frame" aria-busy="true" aria-label="Loading">
				<div className="auth-boot auth-boot--loader">
					<MairkoLoader />
				</div>
			</div>
		);
	}

	return (
		<div className="app-frame">
			{workspace === "chat" || !setupDone ? (
				<MairkoChatView
					userName={profileLabel}
					firstName={user?.firstName ?? session.profile.firstName}
					userImageUrl={profileImageUrl}
					threads={mairkoThreads}
					activeThreadId={activeMairkoThreadId}
					draft={mairkoDraft}
					typing={mairkoTyping}
					onDraftChange={setMairkoDraft}
					onSubmit={sendMairkoText}
					onEditMessage={editMairkoMessage}
					onRetryMessage={retryMairkoMessage}
					onSelectThread={setActiveMairkoThreadId}
					onNewChat={newMairkoChat}
					onPinThread={pinMairkoThread}
					onRenameThread={renameMairkoChat}
					onDeleteThread={deleteMairkoChat}
					onOpenDesk={() => {
						if (!setupDone) return;
						setWorkspace("desk");
					}}
					onOpenAgent={openAgentChat}
					onOpenSearch={() => {
						if (!setupDone) return;
						setSearchOpen(true);
					}}
					onOpenSettings={() => openSettings("account")}
					onOpenBilling={() => openSettings("billing")}
					onSignOut={() => setSignOutOpen(true)}
					themePref={themePref}
					onCycleTheme={cycleTheme}
					surface={chatSurface}
					onSurfaceChange={(next) => {
						setChatSurface(next);
						if (!selectedIntegration) return;
						if (next === "connectors") {
							const isConnector = connections.connectors.some(
								(c) => c.id === selectedIntegration.id,
							);
							if (!isConnector) setSelectedIntegration(null);
						} else if (next === "channels") {
							const isChannel = connections.channels.some(
								(c) => c.id === selectedIntegration.id,
							);
							if (!isChannel) setSelectedIntegration(null);
						} else if (next === "sites" || next === "agents" || next === "messages") {
							setSelectedIntegration(null);
						}
					}}
					setupDone={setupDone}
					connectors={connections.connectors}
					connectorsView={connectorsView}
					connectorsFilter={connectorsFilter}
					onConnectorsViewChange={setConnectorsView}
					onConnectorsFilterChange={setConnectorsFilter}
					channels={connections.channels}
					channelsView={channelsView}
					channelsFilter={channelsFilter}
					onChannelsViewChange={setChannelsView}
					onChannelsFilterChange={setChannelsFilter}
					selectedIntegration={
						chatSurface === "connectors" || chatSurface === "channels"
							? selectedIntegration
							: null
					}
					onSelectIntegration={(item, kind) => {
						openIntegration(item, kind);
					}}
					onClearIntegration={() => {
						setOauthFlash(null);
						setSelectedIntegration(null);
					}}
					platformRowFor={connections.providerFor}
					onConnectionChange={connections.refresh}
					oauthFlash={oauthFlash?.text ?? null}
					oauthFlashTone={oauthFlash?.tone ?? "ok"}
					sitePreviewUrl={sitePreviewUrl}
					onCloseSitePreview={() => setSitePreviewUrl(null)}
					onOpenSitePreview={(url) => setSitePreviewUrl(url)}
					onHandoffPatch={patchMairkoHandoff}
					onHandoffDecision={handleMairkoHandoffDecision}
					onSiteClonePatch={patchMairkoSiteClone}
					onSiteDesignPatch={patchMairkoSiteDesign}
					onPublishSite={(siteId, siteName) =>
						sendMairkoText(
							`Publish the hosted site "${siteName}" (siteId ${siteId}).`,
						)
					}
					onPublishDecision={handlePublishDecision}
					onSiteCloneCard={handleSiteCloneCard}
					onCompanyResearchPatch={patchMairkoCompanyResearch}
					onConfirmCompanyPack={(packId) => void confirmPack(packId)}
					confirmingPackId={confirmingPackId}
					onWorkingCopyYes={onWorkingCopyYes}
					workingCopyCheckout={workingCopyCheckout}
					onWorkingCopyCheckoutChange={setWorkingCopyCheckout}
					onWorkingCopyPaid={() => void onWorkingCopyPaid()}
					onWorkingCopyLater={onWorkingCopyLater}
					onCardAction={onCardAction}
				/>
			) : (
			<div
				className={cn("app", leftOpen && "app--sidebar-open")}
			>
				<aside
					className={cn(
						"sidebar",
						!leftOpen && !narrow && "sidebar--collapsed",
					)}
					aria-label="Workspace"
					aria-hidden={!leftOpen}
					inert={!leftOpen ? true : undefined}
				>
					<div className="sidebar-top">
						<div className="brand-lockup">
							<img
								className="brand-logo brand-logo--dark"
								src="/brand/mairko-logo-primary.svg"
								alt="Mairko"
								height={28}
							/>
							<img
								className="brand-logo brand-logo--light"
								src="/brand/mairko-logo-onlight.svg"
								alt="Mairko"
								height={28}
							/>
						</div>
						<OrgSwitcher
							onManageMembers={() => openSettings("members")}
							onWorkspaceSettings={() => openSettings("workspace")}
						/>
					</div>

					<div className="sidebar-scroll">
						<SidebarNeedsYou
							approvals={pendingApprovals}
							onMessageAgent={openAgentChat}
						/>
						<SidebarRecents
							recents={recentChats}
							activeAgentId={agentId}
							chatOpen={
								nav === "agents" && agentsSurface === "chat"
							}
							busyAgentIds={busyAgentIds}
							onOpen={openAgentChat}
							onOpenTeam={backToAgentsHub}
						/>
					</div>

					<div className="sidebar-foot">
						<div className="sidebar-foot-meters">
							<SidebarUsage
								onOpenBilling={() => openSettings("billing")}
							/>
							<button
								type="button"
								className={cn(
									"side-activity-btn",
									activityBusy > 0 && "side-activity-btn--live",
								)}
								onClick={() => setActivityOpen(true)}
							>
								<span className="side-activity-btn-left">
									<ActivityIcon className="size-4" />
									Live activity
								</span>
								{activityBusy > 0 ? (
									<span className="side-activity-badge">
										{activityBusy}
									</span>
								) : (
									<span className="side-activity-idle">Idle</span>
								)}
							</button>
						</div>
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<button type="button" className="profile">
									{profileImageUrl ? (
										<img
											className="profile-avatar profile-avatar--img"
											src={profileImageUrl}
											alt=""
											width={32}
											height={32}
										/>
									) : (
										<div className="profile-avatar" aria-hidden>
											{profileInitial}
										</div>
									)}
									<div className="profile-copy">
										<p className="profile-name">
											{profileLabel || "You"}
											<PlanBadge />
										</p>
										<p className="profile-sub">
											{tenantName ??
												session.profile.company ??
												"Workspace"}
										</p>
									</div>
								</button>
							</DropdownMenuTrigger>
							<DropdownMenuContent
								align="start"
								side="top"
								className="w-56"
							>
								<DropdownMenuLabel>Workspace</DropdownMenuLabel>
								<DropdownMenuItem
									onClick={() => openSettings("account")}
								>
									<SettingsIcon className="size-4" />
									Settings
								</DropdownMenuItem>
								<DropdownMenuItem
									onClick={() => openSettings("members")}
								>
									<UsersIcon className="size-4" />
									Members
								</DropdownMenuItem>
								<DropdownMenuItem
									onClick={() => openSettings("billing")}
								>
									<CreditCardIcon className="size-4" />
									Usage & billing
								</DropdownMenuItem>
								<DropdownMenuItem
									onClick={() => openSettings("notifications")}
								>
									<BellIcon className="size-4" />
									Notifications
								</DropdownMenuItem>
								<DropdownMenuItem
									onClick={() => openSettings("security")}
								>
									<ShieldIcon className="size-4" />
									Security
								</DropdownMenuItem>
								{showAdminEntry && (
									<>
										<DropdownMenuSeparator />
										<DropdownMenuLabel>Mairko</DropdownMenuLabel>
										<DropdownMenuItem onClick={enterAdmin}>
											<ShieldIcon className="size-4" />
											Firm admin
										</DropdownMenuItem>
									</>
								)}
								<DropdownMenuSeparator />
								<DropdownMenuItem onClick={cycleTheme}>
									Theme · {themePref}
								</DropdownMenuItem>
								<DevResetTrigger variant="menu" onClick={openReset} />
								<DropdownMenuItem
									onClick={() => setSignOutOpen(true)}
								>
									<LogOutIcon className="size-4" />
									Sign out
								</DropdownMenuItem>
							</DropdownMenuContent>
						</DropdownMenu>
					</div>
				</aside>

				<main className="main">
					{narrow && leftOpen ? (
						<button
							type="button"
							className="mc-sidebar-scrim"
							aria-label="Close sidebar"
							onClick={() => setLeftOpen(false)}
						/>
					) : null}
					{nav === "agents" &&
						(agentsSurface === "hub" ? (
							<>
								<ShellToolbar {...toolbarProps} />
								<AgentsHub
									setupDone={setupDone}
									onOpen={openAgentChat}
								/>
							</>
						) : (
							<>
								<ShellToolbar {...toolbarProps} />
								<AgentChat
									agent={agent}
									messages={messages}
									draft={draft}
									onDraftChange={setDraft}
									onSubmit={sendText}
									suggestions={suggestions}
									canType={canType}
									typing={typing}
									progress={progress}
									readyLabel={
										progress
											? undefined
											: setupDone || agentId !== "onboarding"
												? "Ready when you are"
												: undefined
									}
									onOpenInfo={() => openAgentInfo(agentId)}
									onBack={setupDone ? backToAgentsHub : undefined}
									onCardAction={
										agentId === "onboarding" ? onCardAction : undefined
									}
									onCompanyResearchPatch={patchAgentCompanyResearch}
									onSiteDesignPatch={patchMairkoSiteDesign}
									onConfirmCompanyPack={(packId) => void confirmPack(packId)}
									confirmingPackId={confirmingPackId}
									placeholder={
										meta?.placeholder ?? `Message ${agent.name}…`
									}
									composerHint="Return to send · Shift-Return for newline"
								/>
							</>
						))}

					{nav === "connectors" &&
						(selectedIntegration ? (
							<>
								<ShellToolbar {...toolbarProps} />
								<IntegrationDetail
									item={selectedIntegration}
									kind="connector"
									onBack={() => {
										setOauthFlash(null);
										setSelectedIntegration(null);
									}}
									platformRow={connections.providerFor(
										selectedIntegration.id,
									)}
									onConnectionChange={connections.refresh}
									oauthFlash={oauthFlash?.text ?? null}
									oauthFlashTone={oauthFlash?.tone ?? "ok"}
								/>
							</>
						) : (
							<>
								<ShellToolbar {...toolbarProps} />
								<ConnectorsHub
									view={connectorsView}
									filter={connectorsFilter}
									onViewChange={setConnectorsView}
									onFilterChange={setConnectorsFilter}
									onOpen={(item) => openIntegration(item, "connector")}
									items={connections.connectors}
								/>
							</>
						))}

					{nav === "sites" && (
						<>
							<ShellToolbar {...toolbarProps} />
							<SitesHub
								onOpenPreview={(url) => {
									setSitePreviewUrl(url);
									setWorkspace("chat");
									setChatSurface("messages");
								}}
								onPublish={(siteId, siteName) => {
									setWorkspace("chat");
									setChatSurface("messages");
									sendMairkoText(
										`Publish the hosted site "${siteName}" (siteId ${siteId}).`,
									);
								}}
							/>
						</>
					)}

					{nav === "channels" &&
						(selectedIntegration ? (
							<>
								<ShellToolbar {...toolbarProps} />
								<IntegrationDetail
									item={selectedIntegration}
									kind="channel"
									onBack={() => {
										setOauthFlash(null);
										setSelectedIntegration(null);
									}}
									platformRow={connections.providerFor(
										selectedIntegration.id,
									)}
									onConnectionChange={connections.refresh}
									oauthFlash={oauthFlash?.text ?? null}
									oauthFlashTone={oauthFlash?.tone ?? "ok"}
								/>
							</>
						) : (
							<>
								<ShellToolbar {...toolbarProps} />
								<ChannelsHub
									view={channelsView}
									filter={channelsFilter}
									onViewChange={setChannelsView}
									onFilterChange={setChannelsFilter}
									onOpen={(item) => openIntegration(item, "channel")}
									items={connections.channels}
								/>
							</>
						))}
				</main>
			</div>
			)}

			<AgentInfoModal
				agentId={infoAgentId}
				open={infoAgentId !== null}
				onOpenChange={(open) => {
					if (!open) setInfoAgentId(null);
				}}
				onMessage={() => {
					if (infoAgentId) openAgentChat(infoAgentId);
				}}
				providers={
					connections.state.status === "ready"
						? connections.state.providers
						: undefined
				}
			/>

			<ActivityModal
				open={activityOpen}
				onOpenChange={setActivityOpen}
				setupDone={setupDone}
				onboardingStep={session.step}
				onOpenAgent={openAgentChat}
			/>

			<CommandPalette
				open={searchOpen && setupDone}
				onOpenChange={setSearchOpen}
				setupDone={setupDone}
				onOpenAgent={openAgentChat}
				onOpenIntegration={openIntegration}
				onOpenNav={goNav}
				onOpenTeam={backToAgentsHub}
				onOpenBilling={() => openSettings("billing")}
				onOpenActivity={() => setActivityOpen(true)}
			/>

			<SignOutConfirm
				open={signOutOpen}
				onOpenChange={setSignOutOpen}
			/>
			{resetDialog}

			<SettingsModal
				open={settingsOpen}
				tab={settingsTab}
				onTabChange={setSettingsTab}
				onOpenChange={setSettingsOpen}
				session={session}
				risk={risk}
				notifications={notifications}
				themePref={themePref}
				onCycleTheme={cycleTheme}
				onSaveAccount={(patch) => {
					setSession((prev) => ({
						...prev,
						profile: { ...prev.profile, ...patch },
					}));
				}}
				onSaveWorkspace={(patch) => {
					setSession((prev) => ({
						...prev,
						profile: { ...prev.profile, ...patch },
					}));
				}}
				onSaveRisk={setRisk}
				onSaveNotifications={setNotifications}
			/>
		</div>
	);
}

function ThemeIcon({ preference }: { preference: ThemePreference }) {
	switch (preference) {
		case "light":
			return (
				<svg viewBox="0 0 16 16" fill="none" aria-hidden>
					<circle cx="8" cy="8" r="3.2" stroke="currentColor" strokeWidth="1.4" />
					<path
						d="M8 1.5v1.4M8 13.1v1.4M1.5 8h1.4M13.1 8h1.4M3.2 3.2l1 1M11.8 11.8l1 1M3.2 12.8l1-1M11.8 4.2l1-1"
						stroke="currentColor"
						strokeWidth="1.4"
						strokeLinecap="round"
					/>
				</svg>
			);
		case "dark":
			return (
				<svg viewBox="0 0 16 16" fill="none" aria-hidden>
					<path
						d="M12.8 9.2A5.2 5.2 0 0 1 6.8 3.2 5.4 5.4 0 1 0 12.8 9.2Z"
						stroke="currentColor"
						strokeWidth="1.4"
						strokeLinejoin="round"
					/>
				</svg>
			);
		case "system":
			return (
				<svg viewBox="0 0 16 16" fill="none" aria-hidden>
					<rect
						x="2"
						y="3"
						width="12"
						height="8.5"
						rx="1.4"
						stroke="currentColor"
						strokeWidth="1.4"
					/>
					<path
						d="M6 13.5h4"
						stroke="currentColor"
						strokeWidth="1.4"
						strokeLinecap="round"
					/>
				</svg>
			);
		default: {
			const _exhaustive: never = preference;
			return _exhaustive;
		}
	}
}

